<template>
  <div class="group-vehicle-spotlight">
    <div class="mb-5 flex items-center justify-between">
      <div>
        <div class="text-lg font-semibold">Vehicles</div>
      </div>
      <Avatar
        icon="car"
        color="brand"
        size="lg"
        no-border
      />
    </div>

    <div
      v-if="vehicles.length"
      class="flex flex-col gap-3"
    >
      <div
        v-for="vehicle in vehicles.slice(0, 3)"
        :key="vehicle.name"
        class="group-vehicle-row"
      >
        <div class="flex h-16 w-24 shrink-0 items-center justify-center rounded-lg bg-secondary p-2">
          <Image
            :src="vehicle.imageUrl"
            :alt="vehicle.name"
            image-class="h-full w-full object-contain"
          >
            <template #fallbackContent>
              <Avatar
                icon="car"
                color="brand"
                size="lg"
                no-border
              />
            </template>
          </Image>
        </div>
        <div class="flex min-w-0 flex-1 items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="truncate font-medium">{{ vehicle.name }}</div>
          </div>
          <div class="text-right">
            <div class="text-2xl font-semibold">{{ vehicle.deals }}</div>
            <div class="text-xs text-secondary">Deals</div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="rounded-lg bg-secondary p-6 text-center text-sm text-secondary"
    >
      No vehicle winners for this range.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

// components
import Avatar from '@/components/avatar/Avatar.vue'
import Image from '@/components/image/Image.vue'

// types
import type { GroupDashboardDealership } from '@/types/domain/groupDashboard'

/** Aggregated vehicle display row. */
interface VehicleSpotlightRow {
  name: string
  deals: number
  dealerships: number
  imageUrl?: string
}

/** Dealership rows used to aggregate vehicle winners. */
const props = defineProps({
  dealerships: {
    type: Array as PropType<GroupDashboardDealership[]>,
    required: true
  }
})

/** Most sold vehicles aggregated by make and model. */
const vehicles = computed(() => {
  const rows = new Map<string, VehicleSpotlightRow>()

  props.dealerships.forEach(item => {
    const vehicle = item.stats.mostSoldVehicle

    // Skip dealerships that do not have a vehicle winner in the API response.
    if (!vehicle?.make && !vehicle?.model) {
      return
    }

    const name = [vehicle.make, vehicle.model].filter(Boolean).join(' ')
    const existing = rows.get(name)
    const imageUrl = vehicle.imageUrl ?? vehicle.imageURL ?? vehicle.vehicleImageUrl ?? vehicle.image ?? undefined

    // Merge repeated vehicle winners across dealerships into one spotlight row.
    if (existing) {
      existing.deals += vehicle.deals
      existing.dealerships += 1
      existing.imageUrl = existing.imageUrl ?? imageUrl
      return
    }

    rows.set(name, {
      name,
      deals: vehicle.deals,
      dealerships: 1,
      imageUrl
    })
  })

  return [...rows.values()].sort((a, b) => b.deals - a.deals)
})
</script>

<style scoped>
.group-vehicle-spotlight {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  padding: var(--s-5);
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

.group-vehicle-row {
  display: flex;
  gap: var(--s-3);
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 1.35rem;
  padding: var(--s-3);
  background: rgba(255, 255, 255, 0.5);
}

:global(body.dark .group-vehicle-spotlight) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

:global(body.dark .group-vehicle-row) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
}
</style>
